/* eslint-disable */
import { useDispatch } from 'react-redux';
import { useMultiplePo } from './contexts/MultiplePoContext';
import MultiPoContent from './multiPoContent';
import { modalActions } from '@/Actions';
import { BaseModal } from '../BaseModal';
import { Button } from '@/Components/ui/button';
import { getHigherUnitName, getHigherUnitAndQuantity, unitMap } from '@/Helpers';
import AddProductModal from './AddProductModal';
import { buttonConfig } from './config/buttonConfig';
import ConfirmationModal from './ConfirmationModal';
import { useEffect, useRef, useState } from 'react';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices } from '@/Services';
import { ProductionPlanModal } from '../ProductionPlanModal';
import { urls } from '@/Constants';
import moment from 'moment';
import { parseData } from './clipboardUtils';
import Heading from './Heading';
import { Trash2, CheckCheck, ArrowLeft, RefreshCw } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { transformLongTermPlanApiResponse, getLCM, calculateRowVariance, isNotMultipleOf } from './utils/utils';
import productionPlanServices from '@/Services/Analytics Services/production-plan.services';
import { CustomModal } from '../CustomModal';
import { staticTableTransformers } from './config/tableStaticConfig';
import { useAuth } from '@/Components/Auth/Login/auth';

// Icon map for button icons
const iconMap = {
	reset: Trash2,
	check: CheckCheck,
	arrowLeft: ArrowLeft,
	refresh: RefreshCw,
};

const BaseMultipleModal = (props) => {
	const {
		mode = {},
		openModal = false,
		modeName = '',
		navSelectedStore = {},
		sortingType = 'AUTO',
		// embedded: render without the BaseModal wrapper (used by the Create
		// Orders stepper flow); on close, notify the parent instead of toggling
		// the redux multiplePoModal.
		embedded = false,
		onEmbeddedClose = null,
	} = props;
	const toggleModal = useDispatch();
	const { appConfig = {}, userConfig = {} } = useAuth();
	const { configs = {} } = appConfig || {};
	const customerId = appConfig?.customerId;
	const productionPlanModal = useDispatch();
	const {
		dispatch,
		state,
		tableData,
		lists,
		setLists,
		ppId,
		changeQtyRef,
		productList,
		batches,
		selectedPOs,
		setTableData,
		editMode,
		viewMode,
		storeList,
		prevTableData,
		setPrevTableData,
		initialTableData,
		initialFieldsData,
		initialStateSet,
		originalRowsRef,
		setSearchQuery,
		workerAllocations,
		setWorkerAllocations,
		setSortColumn,
		setSortType,
		orderwise,
	} = useMultiplePo();
	const [confirmationModal, setConfirmationModal] = useState({
		isOpen: false,
		message: '',
		heading: '',
		isAlert: false,
		alertType: 'info',
		handleCloseConfirmation: () => {
			setConfirmationModal({
				isOpen: false,
				message: '',
				heading: '',
				isAlert: false,
				alertType: 'info',
			});
		},
		handleConfirmSave: () => {
			setConfirmationModal({
				isOpen: false,
				message: '',
				heading: '',
				isAlert: false,
				alertType: 'info',
			});
		},
		customButton: [],
		showCrossBtn: false,
		isLoading: false,
	});
	const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
	const isGeneratingPlanRef = useRef(false);

	const handleClose = () => {
		dispatch({
			type: 'RESET',
		});
		setTableData({
			headers: [],
			rows: [],
		});
		setPrevTableData({});
		setLists({});
		changeQtyRef.current = true;
		setConfirmationModal({
			isOpen: false,
			message: '',
			heading: '',
			isAlert: false,
			alertType: 'info',
			isLoading: false,
		});
		initialStateSet.current = false;
		if (embedded) {
			onEmbeddedClose?.();
		} else {
			toggleModal(modalActions?.toggleMultiplePoModal(false));
		}
		setWorkerAllocations({});
		originalRowsRef.current = null;
		setSortColumn(null);
		setSortType(null);
		isGeneratingPlanRef.current = false;
	};

	useEffect(() => {
		if (Object.values(navSelectedStore)?.length > 0) {
			dispatch({
				type: 'SET_STATE',
				payload: { ...navSelectedStore },
			});
		}
	}, [navSelectedStore]);

	// Generic initial data handler for modes that pass table data directly (e.g., longTermProduction edit)
	useEffect(() => {
		if (initialTableData?.headers && initialTableData?.rows && editMode) {
			setTableData(initialTableData);
		}
		if (initialFieldsData && editMode) {
			dispatch({
				type: 'SET_STATE',
				payload: initialFieldsData,
			});
			if (initialFieldsData.workerAllocations) {
				setWorkerAllocations(initialFieldsData.workerAllocations);
			}
		}
	}, [initialTableData, initialFieldsData, editMode]);

	useEffect(() => {
		const availableStoreList = (lists?.storeList?.length || 0) > 0
			? lists?.storeList
			: lists?.kitchenList;
		if (
			(editMode || viewMode || mode?.usePreloadedPOs === true) &&
			(availableStoreList?.length || 0) > 0 &&
			selectedPOs?.length > 0
		) {
			const buildSig = [
				(availableStoreList || []).map((s) => s?.id).join(','),
				(selectedPOs || []).map((p) => p?.id).join(','),
			].join('|');
			if (initialStateSet.current === buildSig) {
				return;
			}
			initialStateSet.current = buildSig;
			if (orderwise) {
				const transformer = staticTableTransformers.orderWiseEdit;
				const { headers, rows } = transformer
					? transformer(
						mode,
						productList,
						batches,
						state,
						lists,
						selectedPOs
					)
					: { headers: [], rows: [] };
				const fromStoreId = selectedPOs[0]?.fromStore;
				const filteredStoreList = fromStoreId
					? availableStoreList?.filter(
						(store) => store?.parentStore === fromStoreId
					)
					: availableStoreList;
				const { fieldsData } = parseData(
					selectedPOs,
					filteredStoreList,
					productList,
					viewMode
				);

				const initialOrderInfo = {};
				selectedPOs.forEach((po) => {
					initialOrderInfo[po.id] = {
						isPriority: po.priority || false,
					};
				});
				// summaryMode (e.g. sortingOrderSummary): the modal's
				// header fields (kitchen / shift / date) come from the
				// caller's initialState. parseData's fieldsData targets
				// the orderwise PO flow and stamps `date` as a
				// `DD-MM-YYYY` STRING + sets fromStore/internalShift
				// from the PO — both of which would clobber our Date
				// object and our null kitchen/shift placeholders.
				// Skip those keys in summaryMode.
				const preloadPayload = mode?.summaryMode
					? { orderInfo: initialOrderInfo }
					: { ...fieldsData, orderInfo: initialOrderInfo };
				dispatch({
					type: 'SET_STATE',
					payload: preloadPayload,
				});

				setTableData({
					headers: (viewMode || mode?.summaryMode)
						? headers?.filter((item) => item?.id !== 'Actions')
						: headers,
					rows,
				});
			} else {
				const fromStoreId = selectedPOs[0]?.fromStore;
				const filteredStoreList = fromStoreId
					? availableStoreList?.filter(
						(store) => store?.parentStore === fromStoreId
					)
					: availableStoreList;
				storeList.current = filteredStoreList;

				// summaryMode (sortingOrderSummary): the destination stores are NOT
				// in the loaded BASE kitchen list, so filtering availableStoreList
				// gives nothing. Build the store list from the POs themselves
				// (each PO carries storeId + storeName), deduped by storeId, and
				// enrich from the loaded list when a matching store exists.
				const summaryStoreList = mode?.summaryMode
					? Array.from(
						new Map(
							(selectedPOs || [])
								.filter((po) => po?.storeId != null)
								.map((po) => [po.storeId, po])
						).values()
					).map((po) => {
						const match =
							filteredStoreList?.find(
								(s) => Number(s?.id) === Number(po.storeId)
							) ||
							availableStoreList?.find(
								(s) => Number(s?.id) === Number(po.storeId)
							);
						return (
							match || {
								id: po.storeId,
								name: po.storeName,
								priority: po.priority,
							}
						);
					})
					: filteredStoreList;
				const { headers, rows, fieldsData } = parseData(
					selectedPOs,
					summaryStoreList,
					productList,
					viewMode
				);
				// summaryMode (sortingOrderSummary): kitchen / shift / date come
				// from the caller's initialState. parseData's fieldsData sets
				// `fromStore` (and a DD-MM-YYYY string `date`) — dispatching it
				// changes state.fromStore, which re-fires the field-fetch effect
				// in useMultiPoApi → refetches lists → this table effect re-runs
				// and the table flickers/re-sets. Skip it in summaryMode.
				if (!mode?.summaryMode) {
					dispatch({
						type: 'SET_STATE',
						payload: fieldsData,
					});
				}
				setTableData({
					// summaryMode / viewMode: hide the "Actions" column.
					headers: (viewMode || mode?.summaryMode)
						? headers?.filter((item) => item?.id !== 'Actions')
						: headers,
					rows: rows,
				});
			}
		}
	}, [editMode, viewMode, lists?.storeList, lists?.kitchenList, selectedPOs, orderwise, mode?.usePreloadedPOs]);

	const proceedGenerateProductionPlan = async (orderData = []) => {
		if (isGeneratingPlanRef.current) {
			return;
		}
		isGeneratingPlanRef.current = true;
		try {
			await runProceedGenerateProductionPlan(orderData);
		} finally {
			isGeneratingPlanRef.current = false;
		}
	};

	const runProceedGenerateProductionPlan = async (orderData = []) => {
		const idsList = orderData?.map((item) => item?.id);
		if (sortingType === 'MANUAL') {
			try {
				orderData.map((order) => {
					order?.items?.map((item) => {
						if (!isNaN(Number(item.unit))) {
							const [higherQuantity, higherUnit] = getHigherUnitAndQuantity(item.qty, unitMap[Number(item?.unit)]);
							item.qty = higherQuantity;
							item.unit = higherUnit;
						}
						const [higherQuantity, higherUnit] = getHigherUnitAndQuantity(item.qty, item?.unit);
						item.qty = higherQuantity;
						item.unit = higherUnit;
					})
				})
				handleClose();
				toggleModal(
					modalActions?.toggleMultiplePoModal(true, {
						mode: 'sortingOrderSummary',
						navSelectedStore,
						selectedPOs: orderData,
						summaryMode: true,
						initialState: {
							kitchen: state?.fromStore ?? null,
							date: state?.expectedDeliveryDate
								? new Date(state.expectedDeliveryDate)
								: new Date(),
							...(state?.internalShift != null && { shift: state.internalShift }),
						},
					})
				);
			} catch (e) {
				console.error('MANUAL sortingType: failed to load POs', e);
				notificationServices.generateNotification({
					type: 'error',
					message: 'Could not load the selected purchase orders. Please retry.',
				});
			}
			return;
		}

		const req = {
			data: {
				poIds: idsList,
				storeId: state?.fromStore,
				...(state?.internalShift && { shiftId: state?.internalShift }),
				date: moment(state?.expectedDeliveryDate).format('YYYY-MM-DD'),
			},
		};
		try {
			const response =
				await inventoryAnalyticsServices.fetchProductionPlan(req);
			if (response?.statusCode === 200) {
				if (response?.planGenerated === true) {
					notificationServices.generateNotification({
						type: 'success',
						message: response?.message ?? 'Plan generated successfully',
					});
					handleClose();
					return;
				}
				productionPlanModal(
					modalActions?.toggleProductionPlanModal(true, {
						idsList,
						storeId: state?.fromStore,
						date: moment(state?.expectedDeliveryDate).format(
							'YYYY-MM-DD'
						),
						...(state?.internalShift && { shiftId: state?.internalShift }),
						listData: response?.data,
					})
				);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.errorMessage ?? response?.message,
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleProductionPlanClose = async (ppId) => {
		const id =
			typeof ppId === 'string' || typeof ppId === 'number'
				? ppId
				: undefined;

		if (modeName === 'longTermProduction') {
			if (id === undefined || id === '') {
				const refreshRes =
					await productionPlanServices.getLongTermProductionPlan({
						params: { id: state?.planId },
					});
				if (refreshRes?.statusCode === 200) {
					const { headers, rows, initialState } =
						transformLongTermPlanApiResponse(
							refreshRes.data,
							productList,
							batches
						);
					setTableData({ headers, rows });
					dispatch({
						type: 'SET_STATE',
						payload: {
							...initialState,
							generatedPlans: refreshRes?.data?.generatedPlans,
						},
					});
				}
			}
		} else {
			if (id !== undefined && id !== '') {
				const dateParam = state?.expectedDeliveryDate
					? `&dateRange=${encodeURIComponent({
						active: true,
						from: state.expectedDeliveryDate,
						to: state.expectedDeliveryDate,
					})}`
					: '';
				const url = `${urls?.EXTENSION}${urls?.PRODUCTION_PLANS
					}?id=${encodeURIComponent(id)}${dateParam}`;
				window.open(url, '_blank');
				handleClose();
			} else {
				console.log('production plan id is not generated ');
				handleClose();
			}
		}
	};

	// Check if production plan button should be disabled (expected delivery date is before today)
	const disableProductionPlanBtn = (expectedDeliveryDate) => {
		if (!expectedDeliveryDate) return false;
		const today = moment().startOf('day');
		const deliveryDate = moment(expectedDeliveryDate).startOf('day');
		return deliveryDate.isBefore(today);
	};

	const renderBtns = () => {
		// Use editModeButtons if in edit mode and available
		const buttonsConfig =
			(editMode || orderwise) && mode?.editModeButtons
				? mode.editModeButtons
				: mode?.buttons;

		const filteredButtons = buttonsConfig?.filter((btn) => {
			if (btn.onlyStep !== undefined) {
				return state.currentStep === btn.onlyStep;
			}
			if (btn.show !== undefined) {
				return state.currentStep > btn.show;
			}
			return true;
		});

		let leftButtons = [];
		let rightButtons = [];
		const isPositionButtons = buttonsConfig?.find((btn) => btn.position);
		if (isPositionButtons) {
			leftButtons =
				filteredButtons?.filter((btn) => btn.position === 'left') || [];
			rightButtons =
				filteredButtons?.filter((btn) => btn.position !== 'left') || [];
		}

		const renderButton = (btn) => {
			const config = buttonConfig?.[btn.id];
			const isProductionPlanBtn = btn.id === 'generateProductionPlan';
			const isDisabledByDate =
				isProductionPlanBtn &&
				disableProductionPlanBtn(state?.expectedDeliveryDate);
			return (
				<Button
					key={btn.id}
					className={`${btn.style}`}
					variant={btn.type}
					disabled={
						config?.getDisabled?.({
							state,
							tableData,
							originalRowsRef,
						})
						|| isDisabledByDate
					}
					onClick={() =>
						config?.onClick?.({
							state,
							tableData,
							setConfirmationModal,
							lists,
							handleClose,
							proceedGenerateProductionPlan,
							editMode,
							ppId,
							selectedPOs,
							changeQtyRef,
							prevTableData,
							setTableData,
							dispatch,
							setPrevTableData,
							prevTableData,
							productList,
							batches,
							originalRowsRef,
							setSearchQuery,
							workerAllocations,
							setWorkerAllocations,
							orderwise,
						})
					}
				>
					{btn?.icon &&
						iconMap[btn.icon] &&
						(() => {
							const IconComponent = iconMap[btn.icon];
							return <IconComponent className='w-4 h-4 mr-1' />;
						})()}
					{(() => {
						let label = btn.id === 'nextStep' &&
							modeName === 'longTermProduction' &&
							state?.currentStep === 4
							? 'Save Plan'
							: btn?.noDefineLabel
								? btn?.label
								: mode?.summaryMode
									? btn?.label
									: editMode || orderwise
										? `Edit ${btn.label}`
										: `Create ${btn.label}`;

						// When sortingType is MANUAL, rename "Production Plan" → "Primary Packaging Orders"
						if (sortingType === 'MANUAL') {
							label = label.replace('Preview Production Plan', 'Preview Primary Packaging Orders');
						}

						// Custom label replacement based on orderType
						if (state?.orderType) {
							const type = String(state.orderType).toUpperCase();
							if (type === 'PO') {
								label = label.replace('Sales Order', 'Secondary Dispatch Order / PO');
							} else if (type === 'CMS') {
								label = label.replace('Sales Order', 'Primary Dispatch Order / CMS');
							}
							return label;
						}
						return label;
					})()}
				</Button>
			);
		};

		// In the Long Term Production Plan, step 4 ("Plan Quantity") flags any
		// quantity that isn't a multiple of the batch size. Rather than showing the
		// message inline (which shifts the inputs around), we only highlight the
		// offending cells' borders and surface the explanation here, beside Save.
		const isLongTermStep4 =
			modeName === 'longTermProduction' && state?.currentStep === 4;
		let longTermQtyErrorCount = 0;
		// Per-product breakdown of the invalid cells, for the hover tooltip.
		const longTermInvalidByProduct = [];
		if (isLongTermStep4) {
			const tableRows = tableData?.rows || [];
			const today = moment().startOf('day');
			tableRows.forEach((row, rowIndex) => {
				const batch = parseFloat(row.batch) || 0;
				const packSizeVal = 0; // pack size ignored: validity is batch-multiple only
				if (batch <= 0 && packSizeVal <= 0) return;
				const productName =
					row.productName || row.clientVariantId || '(unknown product)';
				const unit = row.unit || '';
				const badCells = [];
				Object.entries(row).forEach(([key, val]) => {
					if (key.startsWith('date_') && val !== false && val !== '') {
						const dateStr = key.replace('date_', '');
						if (moment(dateStr).isBefore(today)) return;
						const qty = parseFloat(val) || 0;
						const batchInvalid = isNotMultipleOf(qty, batch);
						const packInvalid = isNotMultipleOf(qty, packSizeVal);
						if (batchInvalid || packInvalid) {
							longTermQtyErrorCount += 1;
							// Nearest valid multiples of batch, so we can suggest a fix.
							const lower = batch > 0 ? Math.floor(qty / batch) * batch : qty;
							const upper = batch > 0 ? Math.ceil(qty / batch) * batch : qty;
							badCells.push({ date: dateStr, qty, batch, lower, upper });
						}
					}
				});
				if (badCells.length > 0) {
					longTermInvalidByProduct.push({
						rowIndex,
						productName,
						unit,
						batch,
						cells: badCells,
					});
				}
			});
		}

		return (
			<div className='w-full flex flex-col gap-2'>
				{disableProductionPlanBtn(state?.expectedDeliveryDate) &&
					modeName === 'order' && (
						<p className='w-full flex justify-end text-red-500 text-[12px]'>
							Can't generate production plan: Expected delivery
							date is before order date.
						</p>
					)}
				<div
					className={
						mode?.buttonContainerClass ||
						'w-full flex justify-center gap-2'
					}
				>
					{/* Left positioned buttons */}
					{isPositionButtons ? (
						<>
							<div className='flex gap-2 '>
								{leftButtons.map(renderButton)}
							</div>
							<div className='flex items-center gap-3'>
								{isLongTermStep4 && longTermQtyErrorCount > 0 && (
									<Tooltip
										arrow
										placement='top'
										componentsProps={{
											tooltip: {
												sx: {
													bgcolor: '#fff',
													color: '#111',
													border: '1px solid #fecaca',
													boxShadow: 3,
													maxWidth: 360,
													p: 0,
												},
											},
											arrow: { sx: { color: '#fff' } },
										}}
										title={
											<div className='flex flex-col max-h-[260px] overflow-auto'>
												<div className='px-3 py-2 text-red-600 text-[11px] font-bold border-b border-red-100 bg-red-50 sticky top-0'>
													Quantities must be multiples of the
													batch size
												</div>
												<div className='flex flex-col divide-y divide-gray-100'>
													{longTermInvalidByProduct.map((p) => (
														<div
															key={p.rowIndex}
															className='px-3 py-2'
														>
															<div className='text-[12px] font-semibold text-gray-800'>
																{p.productName}{' '}
																<span className='font-normal text-gray-500'>
																	(batch {p.batch}
																	{p.unit
																		? ` ${p.unit}`
																		: ''}
																	)
																</span>
															</div>
															<div className='mt-1 flex flex-col gap-0.5'>
																{p.cells.map((c) => (
																	<div
																		key={c.date}
																		className='text-[11px] text-gray-600 flex justify-between gap-3'
																	>
																		<span>
																			{moment(
																				c.date
																			).format(
																				'DD MMM'
																			)}
																			:{' '}
																			<span className='text-red-600 font-medium'>
																				{c.qty}
																			</span>
																		</span>
																		<span className='text-gray-400'>
																			→ use{' '}
																			{c.lower}{' '}
																			or {c.upper}
																		</span>
																	</div>
																))}
															</div>
														</div>
													))}
												</div>
											</div>
										}
									>
										<span className='text-red-500 text-[12px] font-medium text-right max-w-[340px] leading-tight cursor-help underline decoration-dotted underline-offset-2'>
											{longTermQtyErrorCount}{' '}
											{longTermQtyErrorCount > 1
												? 'quantities are'
												: 'quantity is'}{' '}
											not a multiple of the batch size. Adjust
											the highlighted cells to save.
										</span>
									</Tooltip>
								)}
								{rightButtons.map(renderButton)}
							</div>
						</>
					) : (
						filteredButtons?.map(renderButton)
					)}
				</div>
			</div>
		);
	};

	const handleOpenProductionPlanModal = (data) => {
		productionPlanModal(
			modalActions?.toggleProductionPlanModal(true, {
				storeId: data?.storeId,
				date: moment(data?.date).format('YYYY-MM-DD'),
				listData: data?.data,
				lppId: data?.data?.lppId ?? state?.planId,
				shiftId: state?.shift,
				...(data?.productionPlanId ? { productionPlanId: data?.productionPlanId } : {}),
				shiftDistribution: data?.shiftDistribution,
				hideButtons: data?.hideButtons,
				nonEditable: data?.nonEditable,
			})
		);
	};
	const handleOpenModal = () => {
		setIsAddProductModalOpen(true);
	};

	// Shared inner content: sub-modals + fields/table body. The embedded variant
	// renders this in a plain layout (no BaseModal), with the footer buttons below.
	const renderInnerContent = () => (
		<>
			<AddProductModal
				isOpen={isAddProductModalOpen}
				onClose={() => setIsAddProductModalOpen(false)}
				onAdd={handleAddProduct}
			/>
			<ProductionPlanModal onCloseModal={handleProductionPlanClose} />
			<CustomModal />
			<ConfirmationModal
				isOpen={confirmationModal?.isOpen}
				heading={confirmationModal.heading}
				message={confirmationModal.message}
				onClose={confirmationModal?.handleCloseConfirmation}
				onConfirm={confirmationModal?.handleConfirmSave}
				isAlert={confirmationModal?.isAlert}
				alertType={confirmationModal?.alertType}
				customButton={confirmationModal?.customButton}
				showCrossBtn={confirmationModal?.showCrossBtn}
				isLoading={confirmationModal?.isLoading}
				width={confirmationModal?.width}
			/>
			<MultiPoContent
				fields={mode?.fields}
				mode={mode}
				modeName={modeName}
				setConfirmationModal={setConfirmationModal}
				handleOpenProductionPlanModal={handleOpenProductionPlanModal}
				handelOpenModal={handleOpenModal}
			/>
		</>
	);

	const handleAddProduct = (newProductsData) => {
		const newRows = newProductsData.map((data, index) => {
			const { product, quantity, batch, selectedDays, packSize, packId, _isUnpacked } = data;
			const originalUnit = product.unit || 'gm';
			const newRowIdx = tableData.rows.length + index;

			const newRow = {
				'S.No': newRowIdx + 1,
				productName: product.viewOnlyLabel,
				clientVariantId: product.clientVariantId || '',
				unit: getHigherUnitName(originalUnit) || originalUnit,
				qty: String(quantity),
				batch: batch,
				originalUnit: originalUnit,
				_variantId: product.variantId,
				_productId: product.productId,
				_isManuallyAdded: true,
				planned: quantity,
				finalProduction: quantity,
				variance: 0,
				fulfilledQty: 0,
				Actions: { rowIndex: newRowIdx },
				// Add pack-specific metadata
				packId: packId || null,
				packSize: packSize || null,
				_isUnpacked: _isUnpacked,
				_packSizeQty: packSize?.quantity || 1,
			};

			// Initialize all dates to false, then distribute quantity using batch and pack-aware logic
			const selectedDateIds = selectedDays || [];
			const selectedDateCount = selectedDateIds.length;
			const totalQty = quantity;
			const batchSize = parseFloat(batch) || 0;
			// const packSizeQty = parseFloat(packSize?.quantity) || 1;
			const packSizeQty = 1;
			const unit = (product.unit || 'gm').toLowerCase();

			tableData.headers.forEach((h) => {
				if (String(h.id).startsWith('date_')) {
					newRow[h.id] = false;
				}
			});

			if (selectedDateCount > 0) {
				// Calculate the Constraint Unit (LCM of Batch and Pack)
				const constraintUnit = (batchSize > 0 && packSizeQty > 0)
					? getLCM(batchSize, packSizeQty)
					: (batchSize || packSizeQty || 1);

				if (constraintUnit > 0) {
					const numFullUnits = Math.floor(totalQty / constraintUnit);
					const remainder = totalQty - numFullUnits * constraintUnit;

					const baseUnitsPerDay = Math.floor(numFullUnits / selectedDateCount);
					let extraUnits = numFullUnits % selectedDateCount;

					selectedDateIds.forEach((key, index) => {
						let dailyQty = baseUnitsPerDay * constraintUnit;
						if (extraUnits > 0) {
							dailyQty += constraintUnit;
							extraUnits--;
						}
						if (index === selectedDateCount - 1) {
							dailyQty += remainder;
						}
						newRow[key] = dailyQty;
					});
				} else {
					let qtyPerDate = totalQty / selectedDateCount;
					if (['count', 'pc', 'pcs'].includes(unit)) {
						qtyPerDate = Math.floor(qtyPerDate);
					}
					selectedDateIds.forEach((key) => {
						newRow[key] = qtyPerDate;
					});
				}
			}

			// Finalize variance calculation based on the distributed quantities
			newRow.variance = calculateRowVariance(
				newRow,
				totalQty,
				originalUnit,
				batchSize,
				packSizeQty
			);

			return newRow;
		});

		setTableData({
			...tableData,
			rows: [...tableData.rows, ...newRows],
		});
		setIsAddProductModalOpen(false);
		notificationServices.generateNotification({
			type: 'success',
			message: `${newRows.length} product(s) added to plan. Please Save the plan before generating the production plan.`,
		});
		changeQtyRef.current = false;
	};

	if (embedded) {
		return (
			<div className='flex flex-col h-full min-h-0'>
				<div className='flex-1 min-h-0 overflow-auto'>
					{renderInnerContent()}
				</div>
				<div className='border-t border-gray-200 bg-white p-3'>
					{renderBtns()}
				</div>
			</div>
		);
	}

	return (
		<BaseModal
			{...(modeName !== 'longTermProduction'
				? {
					heading: mode?.summaryMode
						? `Summary ${mode?.heading || 'Multiple PO'}`
						: viewMode
							? `View ${mode?.heading || 'Multiple PO'}`
							: `${editMode || orderwise ? 'Edit' : 'Create'} ${mode?.heading || 'Multiple PO'
							}`,
				}
				: {
					customHeading: () => (
						<Heading
							mode={
								viewMode
									? 'View'
									: (editMode || orderwise)
										? 'Edit'
										: 'Create'
							}
							currentStep={state?.currentStep}
							title={mode?.heading}
							stepper={
								editMode
									? mode?.editModeStepper
									: mode?.defaultSteps
							}
						/>
					),
				})}
			open={openModal}
			handleClose={handleClose}
			renderBtns={viewMode ? () => { } : renderBtns}
			width='100%'
			height='100%'
			customStyles={{
				...((editMode || viewMode) && {
					background: 'rgb(255 254 248)',
				}),
			}}
			disableBackdropClick
			disableEscapeKeyDown
		>
			{renderInnerContent()}
		</BaseModal>
	);
};
export default BaseMultipleModal;
